import { useState, useEffect } from 'react';
import { Users, Activity, CheckCircle, XCircle, Clock } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useFormattedDate } from '../i18n/dateUtils';
import { adminApi } from '../utils/api';
import type { Stats as StatsType, UsageRecord } from '../types';

const Stats = () => {
  const { t } = useTranslation('admin');
  const { t: tCommon } = useTranslation('common');
  const { formatDateTime } = useFormattedDate();
  const [stats, setStats] = useState<StatsType | null>(null);
  const [usageRecords, setUsageRecords] = useState<UsageRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const [statsRes, usageRes] = await Promise.all([
        adminApi.getStats(),
        adminApi.getUsage({ limit: 50 }),
      ]);
      setStats(statsRes.data);
      setUsageRecords(usageRes.data.records);
    } catch (err: any) {
      setError(err.response?.data?.detail || t('stats.error.fetchFailed'));
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-600">
        {error}
      </div>
    );
  }

  const statCards = [
    {
      title: t('stats.cards.totalUsers'),
      value: stats?.total_users || 0,
      icon: Users,
      color: 'bg-blue-500',
    },
    {
      title: t('stats.cards.activeUsers'),
      value: stats?.active_users || 0,
      icon: Activity,
      color: 'bg-green-500',
    },
    {
      title: t('stats.cards.todayCalls'),
      value: stats?.today_calls || 0,
      icon: Clock,
      color: 'bg-orange-500',
    },
  ];

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">{t('stats.title')}</h1>
        <p className="text-gray-600 mt-1">{t('stats.description')}</p>
      </div>

      {/* 统计卡片 */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((card) => {
          const Icon = card.icon;
          return (
            <div key={card.title} className="card">
              <div className="flex items-center gap-4">
                <div className={`p-3 rounded-lg ${card.color} text-white`}>
                  <Icon className="w-6 h-6" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">{card.title}</p>
                  <p className="text-2xl font-bold">{card.value}</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* 调用统计 */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="card">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-green-100 rounded-lg">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">{t('stats.cards.successfulCalls')}</p>
              <p className="text-2xl font-bold text-green-600">
                {stats?.successful_calls || 0}
              </p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-red-100 rounded-lg">
              <XCircle className="w-6 h-6 text-red-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">{t('stats.cards.failedCalls')}</p>
              <p className="text-2xl font-bold text-red-600">
                {stats?.failed_calls || 0}
              </p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-blue-100 rounded-lg">
              <Activity className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">{t('stats.cards.successRate')}</p>
              <p className="text-2xl font-bold text-blue-600">
                {stats && stats.total_api_calls > 0
                  ? `${((stats.successful_calls / stats.total_api_calls) * 100).toFixed(1)}%`
                  : '0%'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* 使用记录 */}
      <div className="card">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('stats.table.title')}</h2>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  {t('stats.table.user')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  {t('stats.table.endpoint')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  {t('stats.table.time')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  {t('stats.table.duration')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  {t('stats.table.status')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {usageRecords.map((record, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">
                    {record.username}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {record.endpoint}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {formatDateTime(record.timestamp)}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {record.duration_ms}ms
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        record.success
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}
                    >
                      {record.success ? tCommon('status.success') : tCommon('status.failed')}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Stats;
